import { useBooleanFlagValue } from '@openfeature/react-sdk';

import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useIqAllowed } from '@/shared/config/useIqAllowed';
import { useSaveToProjectEnabled } from '@/shared/hooks';

/**
 * The workspace the analysis-to-project actions ("Start brief", "Add to project")
 * would write into, or `null` when neither may render: `iq_project_actions` is off,
 * the projects module is off, the user has no IQ access in the current
 * workspace, or no workspace has resolved yet.
 *
 * `iq_project_actions` is what gates these two actions specifically. It sits next to
 * `projects_module` rather than replacing it, because that one gates the projects
 * module everywhere and turning it on for another surface must not surface these.
 *
 * Both actions mint the same artifact and share this gate. Returns the id rather
 * than a boolean so the gate and the value the mint needs stay in one place: the
 * section layout reads it to decide whether it has right-hand actions, the buttons
 * read it to mint.
 */
export const useProjectActionsWorkspaceId = (): string | null => {
  const { isAllowed: isIqAllowed } = useIqAllowed();
  const projectActionsEnabled = useBooleanFlagValue('iq_project_actions', false);
  const saveToProjectEnabled = useSaveToProjectEnabled();
  const workspaceId = useCurrentWorkspaceId();

  if (!isIqAllowed || !projectActionsEnabled || !saveToProjectEnabled) return null;
  return workspaceId;
};
